





































































































































import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import { TicketService } from "@share/common/services/ticket/TicketService";
import { TicketType } from '../enums/TicketTypes';
import { GroupedDataType } from '../enums/GroupedDataTypes';
import { FilterType } from "@common/table/enums/FilterTypes";
import { FilterSetting } from "@common/table/interfaces/FilterSetting";

import BounceSpinner from "@share/common/BounceSpinner.vue";
import SmallDashboardCard from "./SmallDashboardCard.vue";
import BigDashboardCard from "./BigDashboardCard.vue";
import SegmentControl from  "@share/common/segment/Segment.vue";
import QuickFilters from "@common/table/components/filter/QuickFilters.vue";
import ControlWrapper from "@share/common/ControlWrapper.vue";
import ComparisonIndicator from './ComparisonIndicator.vue';
import FilterModal from "@common/table/components/filter/FilterModal.vue";

import { StringHelpers } from "@common/StringHelpers"
import { DateTime } from 'luxon';
import { ChartType } from '../enums/ChartTypes';
import {required} from "vuelidate/lib/validators";
import { ArrayHelpers } from '@common/ArrayHelpers';
import { SelectedItem } from '@common/table/interfaces/SelectedItem';
import { Modal } from '@common/Modal';

interface SelectedFilters {
    beginDate?: string;
    endDate?: string;
    branch?: string;
}

const CREATED_AT_NAME = 'createdAt';
const BRANCH_NAME = 'branch';

@Component({ components: {
    BounceSpinner, SmallDashboardCard, BigDashboardCard, SegmentControl, QuickFilters,
    ControlWrapper, ComparisonIndicator, FilterModal
}})
export default class TicketDashboard extends Vue {
    @Prop({required: true}) ticketService: TicketService;

    // данные, полученные через API
    data: any = null;

    // настройки полей фильтров, задаются в процедуре setFilters
    filters: FilterSetting[] = [];

    // типы графиков, переменные для использования в шаблоне
    doughnut = ChartType.doughnut;
    pie = ChartType.pie;
    horizontalBar = ChartType.horizontalBar;

    // хранит предыдущие выбранные фильтры, при закрытии модального окна без нажатия кнопки Применить
    // значения выбранных фильтров будут восстановлены из этой переменной
    previousSelectedFilters: {
        branch?: SelectedItem;
        dates?: SelectedItem;
    } = {};

    private isLoading = true;
    private isFiltersModalVisible = false;
    selectedType: TicketType = TicketType.idea;

    async mounted() {
        this.setFilters();
        if (!this.loadFiltersFromStorage()) {
            this.setDatesToDefault();
        }
        await this.loadData();
    }

    async loadData() {
        this.isLoading = true;
        try {
            // TODO создать тип для передачи тела запроса
            this.data = await this.ticketService.getStatistic(this.selectedType, {
                beginDate: this.selectedFilters.beginDate,
                endDate: this.selectedFilters.endDate,
                branch: this.selectedFilters.branch
            });
        } catch(e) {
            this.data = null;
            this.confirmReload();
            this.$store.dispatch('reportException', e, {root: true});
        } finally {
            this.isLoading = false;
        }
    }

    async confirmReload() {
        const modal = new Modal({
            title: 'Ошибка',
            contentText: 'Произошла ошибка при получении данных.',
            buttons: [
                {
                text: 'Повторить',
                class: 'is-green',
                returnValue: true,
                },
            ],
        });

        const confirmResult = await modal.showConfirm();

        if (confirmResult === true) {
            this.loadData();
        }
    }

    // выбранные фильтры
    get selectedFilters(): SelectedFilters {
        let beginDate, endDate, branch;

        if (!this.isFiltersModalVisible) {
            const filterCreatedAt = this.filters.find((filter: FilterSetting) => filter.name === CREATED_AT_NAME);
            beginDate = filterCreatedAt?.selectedItem?.start;
            endDate = filterCreatedAt?.selectedItem?.end;

            const filterBranch = this.filters.find((filter: FilterSetting) => filter.name === BRANCH_NAME);
            branch = filterBranch?.selectedItem?.id;
        } else {
            beginDate = this.previousSelectedFilters.dates?.start;
            endDate = this.previousSelectedFilters.dates?.end;
            branch = this.previousSelectedFilters.branch?.id;
        }

        return {
            beginDate: beginDate ? beginDate : undefined,
            endDate: endDate ? endDate : undefined,
            branch
        }
    }

    get segmentData() {
        return [{
            active: this.selectedType === TicketType.idea,
            title: StringHelpers.capitalize(this.$t('tickets.idea').toString()),
            value: TicketType.idea
        },
        {
            active: this.selectedType === TicketType.problem,
            title: StringHelpers.capitalize(this.$t('tickets.problem').toString()),
            value: TicketType.problem
        }]
    }

    get isFilterSet() {
        return Object.keys(this.selectedFilters).length > 0;
    }

    get isIdeas() {
        return this.selectedType === TicketType.idea;
    }

    get chartdataForDate() {
        const data = this.data.groupedData.filter((item: any) => item.type === GroupedDataType.date);
        data.sort((a: any, b: any) => {
            const dateFromA = DateTime.fromISO(a.parameterName),
                dateFromB = DateTime.fromISO(b.parameterName);
            return dateFromA < dateFromB ? - 1 : 1;
        });
        return {
            labels: data.map((item: any) => DateTime.fromISO(item.parameterName)
                .toLocaleString({ month: 'numeric', day: 'numeric', year: 'numeric' })),
            data: data.map((item: any) => item.count)
        };
    }

    get chartdataForStatus() {
        const data = this.data.groupedData.filter((item: any) => item.type === GroupedDataType.status);
        data.sort((a: any, b: any) => {
            return a.count > b.count ? - 1 : 1;
        });
        return { labels: data.map((item: any) => item.parameterName), data: data.map((item: any) => item.count) };
    }

    get chartdataForCategory() {
        const data = this.data.groupedData.filter((item: any) => item.type === GroupedDataType.category);
        data.sort((a: any, b: any) => {
            return a.count > b.count ? - 1 : 1;
        });
        return { labels: data.map((item: any) => item.parameterName), data: data.map((item: any) => item.count) };
    }

    get chartdataForBranch() {
        const data = this.data.groupedData.filter((item: any) => item.type === GroupedDataType.company);
        data.sort((a: any, b: any) => {
            return a.count > b.count ? - 1 : 1;
        });
        return { labels: data.map((item: any) => item.parameterName), data: data.map((item: any) => item.count) };
    }

    async applyFilters() {
        this.isFiltersModalVisible = false;
        this.loadData();
        this.saveFiltersToStorage();
    }

    get lsKey() {
        return this.$store.state.session.backend + this.$store.getters.userId + '_ticket-dashboard-filters';
    }

    @Watch('selectedType')
    onTicketTypeChanged() {
        this.loadData();
        this.saveFiltersToStorage();
    }

    saveFiltersToStorage() {
        const obj = ArrayHelpers.keyBy(this.filters, 'name');
        sessionStorage.setItem(this.lsKey, JSON.stringify({
            dates: obj[CREATED_AT_NAME].selectedItem,
            branch: obj[BRANCH_NAME].selectedItem,
            type: this.selectedType
        }))
    }

    loadFiltersFromStorage() {
        const storageValue = sessionStorage.getItem(this.lsKey);
        if (!storageValue) {
            return false
        }

        try {
            const storageParsedValue = JSON.parse(storageValue);
            if (storageParsedValue.branch) {
                this.setBranch(storageParsedValue.branch);
            }

            if (storageParsedValue.dates) {
                this.setDates(storageParsedValue.dates);
            }

            if (storageParsedValue.type === TicketType.idea || storageParsedValue.type === TicketType.problem) {
                this.selectedType = storageParsedValue.type;
            }
        } catch(err) {
            return false;
        }
        return true;
    }

    setBranch(branch?: SelectedItem) {
        const filter = this.filters.find((filter: FilterSetting) => filter.name === BRANCH_NAME);

        if (filter && filter.selectedItem) {
            filter.selectedItem = branch;
        }
    }

    setDates(dates?: SelectedItem) {
        const filter = this.filters.find((filter: FilterSetting) => filter.name === CREATED_AT_NAME);

        if (filter && filter.selectedItem) {
            filter.selectedItem = dates
        }
    }

    // вычисляет изменение в процентах, положительный результат - увеличение, отрицательный - уменьшение
    comparedToAvg(current: number, previous: number) {
        if (previous === 0) {
            return 0;
        }
        if (current >= previous) {
            return Math.floor(100 * (current / previous - 1));
        }
        return -Math.floor(100 * (1 - current / previous));
    }

    setDatesToDefault() {
        const filterCreatedAt = this.filters.find((filter: FilterSetting) => filter.name === CREATED_AT_NAME);

        if (!filterCreatedAt) {
            console.log(`Не задан фильтр с name="${CREATED_AT_NAME}".`)
            return;
        }

        filterCreatedAt.selectedItem = {
            start: DateTime.local().minus({ days: 30 }).toISODate(),
            end: DateTime.local().toISODate(),
        }

        this.saveFiltersToStorage();
    }

    setSelectQiuckFilter() {
        this.loadData();
        this.saveFiltersToStorage();
    }

    setFilters() {
        this.filters = [
            {
                type: FilterType.dateRange,
                name: CREATED_AT_NAME,
                title: this.$t('tickets.filters.createdAt'),
                data: null,
                selectedItem: {
                    start: '',
                    end: ''
                },
                value: '',
                validations: {
                    start: {
                        required
                    },
                    end: {
                        required
                    }
                }
            },
            {
                type: FilterType.select,
                name: BRANCH_NAME,
                title: this.$t('tickets.filters.branch'),
                data: null,
                ref: 'CompanyStructure',
                condition: {
                    parentId: {
                        '$inQuery': {
                            schema: 'CompanyStructure',
                            where: {
                                parentId: {
                                    '$exists': false
                                }
                            },
                        }
                    }
                },
                titleAddition: {
                    'parentId': ['title']
                },
                selectedItem: {},
                isPresentInQuickFilter: true,
                quickFilterSettings: {
                    title: this.$t('tickets.filters.divisions'),
                    width: '166px',
                    sortingWeight: 50,
                    class: "quick-filter-hide-xl"
                }
            }
        ];
    }

    async resetFilters() {
        this.isFiltersModalVisible = false;
        this.setDatesToDefault();
        this.loadData();
    }

    closeModal() {
        this.isFiltersModalVisible = false;
        // вернем прежние значения фильтров
        this.setBranch(this.previousSelectedFilters.branch);
        this.setDates(this.previousSelectedFilters.dates);
    }

    showModal() {
        const obj = ArrayHelpers.keyBy(this.filters, 'name');
        // запомним значения фильтров
        this.previousSelectedFilters = {
            dates: { ...obj[CREATED_AT_NAME].selectedItem },
            branch: { ...obj[BRANCH_NAME].selectedItem }
        };
        this.isFiltersModalVisible = true;
    }

    formatISODate(date: string) {
        return DateTime.fromISO(date).toLocaleString({ month: 'numeric', day: 'numeric', year: 'numeric' });
    }
}
